<template>
  <div class="auth-layout">
    <!-- Background decoration -->
    <div class="auth-bg">
      <div class="bg-gradient"></div>
      <div class="bg-grid"></div>
    </div>

    <div class="auth-container">
      <!-- Logo and tagline -->
      <div class="auth-header">
        <RouterLink to="/" class="auth-logo">
          <img src="/logo.svg" alt="MonDAI Logo" class="auth-logo-img" />
          <span class="auth-logo-text">
            <span class="logo-accent">{{ appName.split(" ")[0] }}</span>
            {{ appName.split(" ").slice(1).join(" ") }}
          </span>
        </RouterLink>
        <p class="auth-tagline">{{ tagline }}</p>
      </div>

      <!-- Auth card -->
      <div class="auth-card glass">
        <RouterView v-slot="{ Component }">
          <Transition name="auth" mode="out-in">
            <component :is="Component" v-if="Component" />
          </Transition>
        </RouterView>
      </div>

      <!-- Footer -->
      <footer class="auth-footer">
        <p>
          © {{ new Date().getFullYear() }} {{ communityName }}. All rights
          reserved.
        </p>
        <div class="auth-footer-links">
          <RouterLink to="/privacy">Privacy Policy</RouterLink>
          <span>•</span>
          <RouterLink to="/terms">Terms of Service</RouterLink>
        </div>
      </footer>
    </div>

    <!-- Theme toggle -->
    <div class="auth-theme-toggle">
      <ThemeToggle />
    </div>
  </div>
</template>

<script setup lang="ts">
import { RouterLink, RouterView } from "vue-router";
import { config } from "@/config";
import ThemeToggle from "@/components/ui/ThemeToggle.vue";

const appName = config.app.name;
const tagline = config.app.tagline;
const communityName = config.app.communityName;
</script>

<style scoped>
.auth-layout {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

/* Background effects */
.auth-bg {
  position: fixed;
  inset: 0;
  z-index: -1;
}

.bg-gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse at 30% 20%,
      rgba(0, 119, 255, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      ellipse at 70% 80%,
      rgba(0, 119, 255, 0.1) 0%,
      transparent 50%
    );
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 119, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 119, 255, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
}

.auth-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-xl);
  width: 100%;
  max-width: 450px;
}

.auth-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.auth-logo {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.auth-logo-img {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.logo-accent {
  color: var(--color-accent);
}

.auth-tagline {
  color: var(--color-text-secondary);
  font-size: var(--font-size-lg);
}

.auth-card {
  width: 100%;
  padding: var(--spacing-xl);
  border-radius: var(--radius-xl);
}

.auth-footer {
  margin-top: var(--spacing-xl);
  text-align: center;
}

.auth-footer p {
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  margin-bottom: var(--spacing-sm);
}

.auth-footer-links {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: center;
  font-size: var(--font-size-sm);
}

.auth-footer-links a {
  color: var(--color-text-secondary);
}

.auth-footer-links a:hover {
  color: var(--color-accent);
}

.auth-footer-links span {
  color: var(--color-text-muted);
}

.auth-theme-toggle {
  position: fixed;
  top: var(--spacing-lg);
  right: var(--spacing-lg);
}

/* Transitions */
.auth-enter-active,
.auth-leave-active {
  transition: all 0.2s ease;
}

.auth-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.auth-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
